import React, { useState, useEffect } from 'react';
import CalendarIcon from './icons/CalendarIcon';
import CardsIcon from './icons/CardsIcon';
import ListIcon from './icons/ListIcon';
import PrevIcon from './icons/PrevIcon';
import NextIcon from './icons/NextIcon';

interface CalendarNavigationProps {
	activeStartDate: Date;
	locale: string;
	selectedYear: number;
	goToPreviousMonth: () => void;
	goToCurrentMonth: () => void;
	goToNextMonth: () => void;
	handleMonthClick: (month: number) => void;
	handleYearChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
	formatMonthYear: (date: Date) => string;
	view: 'calendar' | 'cards' | 'list';
	setView: (view: 'calendar' | 'cards' | 'list') => void;
	showCalendar: boolean;
	showCards: boolean;
	showList: boolean;
}

const CalendarNavigation: React.FC<CalendarNavigationProps> = ({
																   activeStartDate,
																   locale,
																   selectedYear,
																   goToPreviousMonth,
																   goToCurrentMonth,
																   goToNextMonth,
																   handleMonthClick,
																   handleYearChange,
																   formatMonthYear,
																   view,
																   setView,
																   showCalendar,
																   showCards,
																   showList
															   }) => {
	const [isPopupActive, setIsPopupActive] = useState(false);

	const openPopup = () => {
		setIsPopupActive(true);
		console.log('opening popup');
	};

	const handleClickOutside = (event: MouseEvent) => {
		const target = event.target as HTMLElement;
		if (!target.closest('.fsdpe-calendar-wrapper__date-selector')) {
			setIsPopupActive(false);
		}
	};

	const handleMonthClickAndClosePopup = (month: number) => {
		handleMonthClick(month);
		setIsPopupActive(false);
	};

	useEffect(() => {
		document.addEventListener('click', handleClickOutside);
		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, []);

	// Get the current month and year
	const currentMonth = new Date().getMonth(); // 0-based (Jan = 0, Feb = 1, ...)
	const currentYear = new Date().getFullYear();

	// Check if the current month and year are different from activeStartDate
	const isNotCurrentMonth = activeStartDate.getMonth() !== currentMonth || activeStartDate.getFullYear() !== currentYear;

	return (
		<div className="fsdpe-calendar-wrapper__navigation">
			<div className="fsdpe-calendar-wrapper__navigation__row fsdpe-calendar-wrapper__navigation__row--background">
				<div className="fsdpe-calendar-wrapper__date-selector">
					<div className={"fsdpe-calendar-wrapper__date-selector__button"} onClick={openPopup}>
						<span>Month</span>
						<CalendarIcon />
					</div>

					<div className={`fsdpe-calendar-wrapper__date-selector__popup ${isPopupActive ? 'active' : ''}`}>
						<div className="year-selector">
							<button onClick={() => handleYearChange({ target: { value: selectedYear - 1 } } as React.ChangeEvent<HTMLSelectElement>)}>
								<PrevIcon />
							</button>
							<span>{selectedYear}</span>
							<button onClick={() => handleYearChange({ target: { value: selectedYear + 1 } } as React.ChangeEvent<HTMLSelectElement>)}>
								<NextIcon />
							</button>
						</div>
						<div className="month-selector">
							{Array.from({ length: 12 }, (_, i) => (
								<button
									key={i}
									onClick={() => handleMonthClickAndClosePopup(i + 1)}
									className={activeStartDate.getMonth() === i ? 'active' : ''}
								>
									{new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(0, i))}
								</button>
							))}
						</div>
					</div>
				</div>
				<div className="fsdpe-calendar-wrapper__buttons">
					<span>View:</span>
					{showCalendar && (
						<button className={view === 'calendar' ? 'active' : ''} onClick={() => setView('calendar')}>
							<CalendarIcon />
						</button>
					)}
					{showCards && (
						<button className={view === 'cards' ? 'active' : ''} onClick={() => setView('cards')}>
							<CardsIcon />
						</button>
					)}
					{showList && (
						<button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>
							<ListIcon />
						</button>
					)}
				</div>
			</div>
			<div className="fsdpe-calendar-wrapper__navigation__row fsdpe-calendar-wrapper__navigation__row--background">
				<div>
					<h2>{formatMonthYear(activeStartDate)}</h2>
					{isNotCurrentMonth && (
						<span onClick={goToCurrentMonth}>Go back to current month</span>
					)}
				</div>

				<div className="fsdpe-calendar-wrapper__buttons fsdpe-calendar-wrapper__buttons--small">
					<button onClick={goToPreviousMonth}>
						<PrevIcon />
					</button>
					<button onClick={goToNextMonth}>
						<NextIcon />
					</button>
				</div>
			</div>
		</div>
	);
};

export default CalendarNavigation;
